import { useEffect, useState, useCallback } from "react";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import AgentsService from "../../services/AgentsService/AgentsService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import { normalizeAgentProperties } from "../../utils/NormalizationUtil.ts";
import AddAgentPopup from "../Popup/AddAgentPopup.tsx";
import RemoveAgentPopup from "../Popup/RemoveAgentPopup.tsx";
import AgentsDataGrid from "../../components/DataTables/AgentsDataGrid.tsx";
import { Agent } from "../../types/agent.ts";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";

const OfficesAndAgentsManagement = () => {
  const { jwt } = useAuth();
  const [allOffices, setAllOffices] = useState<{ [key: string]: string }>({});
  const [agentsList, setAgentsList] = useState<Agent[]>([]);
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const [selectedOffice, setSelectedOffice] = useState<string>("");
  const [showAddAgentPopup, setShowAddAgentPopup] = useState<boolean>(false);
  const [showRemoveAgentPopup, setShowRemoveAgentPopup] =
    useState<boolean>(false);
  const [subAgentsIds, setSubAgentsIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const isOfficeSelected = selectedOffice !== "";

  const displayAlert = useCallback((message: string, type: FadeAlertType) => {
    setAlert(message);
    setAlertType(type);
  }, []);

  const fetchOfficesData = useCallback(async () => {
    try {
      const officesAll = await InsuranceService.getAllOffices(jwt, false);
      setAllOffices(officesAll as { [key: string]: string });
    } catch (err: any) {
      displayAlert(err.message, FadeAlertType.error);
    }
  }, [jwt, displayAlert]);

  const fetchAgentsForOffice = useCallback(
    async (officeId: string) => {
      try {
        setLoading(true);
        const ids = await AgentsService.getSubAgentsIdsByAgentId(jwt, officeId);

        const validIds = (ids as string[]).filter(
          (id) => typeof id === "string" && id.trim() !== ""
        );

        setSubAgentsIds(validIds);
        const subAgents: Agent[] = await Promise.all(
          validIds.map(async (agentId): Promise<Agent> => {
            const normalizedAgent = normalizeAgentProperties(
              (await AgentsService.getAgentById(jwt, agentId)) as object
            );
            normalizedAgent.isActive = normalizedAgent.isActive === 1;
            return normalizedAgent;
          })
        );
        setAgentsList(subAgents);
      } catch (err: any) {
        displayAlert(err.message, FadeAlertType.error);
      } finally {
        setLoading(false);
      }
    },
    [jwt, displayAlert]
  );

  useEffect(() => {
    fetchOfficesData();
  }, [fetchOfficesData]);

  const handleOfficeSelectChange = useCallback(
    async (value: string) => {
      setSelectedOffice(value);
      if (value) await fetchAgentsForOffice(value);
    },
    [fetchAgentsForOffice]
  );

  const handleAddAgent = useCallback(
    async (selectedAgents: string[]) => {
      try {
        if (selectedOffice) {
          await AgentsService.saveAgentsToOffice(
            jwt,
            selectedOffice,
            selectedAgents
          );
          displayAlert("Успешен запис", FadeAlertType.success);
          await fetchAgentsForOffice(selectedOffice);
        } else {
          displayAlert("Моля, изберете офис", FadeAlertType.error);
        }
      } catch (err: any) {
        displayAlert(err.message, FadeAlertType.error);
      }
      setShowAddAgentPopup(false);
    },
    [jwt, selectedOffice, fetchAgentsForOffice, displayAlert]
  );

  const handleRemoveAgent = useCallback(
    async (updatedAgents: string[]) => {
      try {
        if (selectedOffice) {
          await AgentsService.saveAgentsToOffice(
            jwt,
            selectedOffice,
            updatedAgents
          );
          displayAlert("Успешен запис", FadeAlertType.success);
          await fetchAgentsForOffice(selectedOffice);
        } else {
          displayAlert("Моля, изберете офис", FadeAlertType.error);
        }
      } catch (err: any) {
        displayAlert(err.message, FadeAlertType.error);
      }
      setShowRemoveAgentPopup(false);
    },
    [jwt, selectedOffice, fetchAgentsForOffice, displayAlert]
  );

  return (
    <>
      <Breadcrumb pageName="Управление на офиси и агенти" />
      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark mt-2 rounded-sm border bg-white">
        <FadeAlert
          alert={alert}
          type={alertType}
          onClose={() => setAlert(null)}
        />

        <SectionHeader title="Управление на офиси и агенти" />

        <div className="p-3">
          {/* Office select + action buttons in one row */}
          <div className="mb-2 flex items-end gap-2">
            <div className="flex-1">
              <label className="mb-1 ml-1 block text-xs font-medium text-black dark:text-white">
                Офис
              </label>
              <SelectGroup
                value={selectedOffice}
                placeholder="Изберете офис"
                options={allOffices}
                onChange={handleOfficeSelectChange}
              />
            </div>
            <button
              className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition ${
                isOfficeSelected
                  ? "bg-primary text-white hover:bg-opacity-90"
                  : "cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-gray-700"
              }`}
              onClick={() => setShowAddAgentPopup(true)}
              disabled={!isOfficeSelected}
            >
              <i className="fa-kit fa-solid-plus-user" />
              Добавяне
            </button>
            <button
              className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition ${
                isOfficeSelected
                  ? "bg-primary text-white hover:bg-opacity-90"
                  : "cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-gray-700"
              }`}
              onClick={() => setShowRemoveAgentPopup(true)}
              disabled={!isOfficeSelected}
            >
              <i className="fa-kit fa-solid-minus-user" />
              Премахване
            </button>
          </div>

          <AgentsDataGrid agentsList={agentsList} isLoading={loading} />
        </div>

        {showAddAgentPopup && (
          <AddAgentPopup
            officeName={allOffices[selectedOffice]}
            agentsIds={subAgentsIds}
            onClose={() => setShowAddAgentPopup(false)}
            onSave={handleAddAgent}
          />
        )}
        {showRemoveAgentPopup && (
          <RemoveAgentPopup
            officeName={allOffices[selectedOffice]}
            agentsIds={subAgentsIds}
            onClose={() => setShowRemoveAgentPopup(false)}
            onSave={handleRemoveAgent}
          />
        )}
      </div>
    </>
  );
};

export default OfficesAndAgentsManagement;
